import React, { useEffect, useState } from "react";
import { useAppContext } from "../../context/AppContext";
import { getCourse, pollCourseUnlock, purchaseCourse } from "../../services/courseService";
import { CourseClassDetailTrans, ScheduleTrans, SuccessTrans, ToasterTrans } from "../../utils/translations";
import PayRub from "../../assets/images/pay1.png";
import PayShek from "../../assets/images/pay2.png";
import Loader from "./Loader";
import "../../styles/landing.css";
import ApproxPrice from "../ui/ApproxPrice";

const PREVIEW_IMAGE_URL =
  "https://res.cloudinary.com/deaqcrhah/image/upload/v1783281920/onceUpOnAMat/utils/ChatGPT_Image_Jul_5_2026_11_02_48_PM_x0zwyy.png";

/**
 * Wraps a premium page: fetches the course by slug and either renders the
 * page (premium role or already purchased) or a paywall with buy buttons.
 * Assumes the caller already guarantees the user is authenticated (e.g. via RequireAuthRoute).
 */
export default function CourseGate({ courseSlug, children, disablePayment = false }) {
  const { language, token, loading: userLoading, setToaster } = useAppContext();
  const t = CourseClassDetailTrans[language] || CourseClassDetailTrans.english;

  const [course, setCourse] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [purchasingProvider, setPurchasingProvider] = useState(null);
  const [purchaseError, setPurchaseError] = useState("");

  useEffect(() => {
    if (userLoading || !token) return;
    let cancelled = false;

    const paymentSuccess = new URLSearchParams(window.location.search).get("payment") === "success";
    if (paymentSuccess) {
      const url = new URL(window.location.href);
      url.searchParams.delete("payment");
      window.history.replaceState({}, "", url.pathname + url.search);
      setProcessingPayment(true);
    }

    (paymentSuccess ? pollCourseUnlock(courseSlug, token) : getCourse(courseSlug, token))
      .then((data) => {
        if (cancelled) return;
        setCourse(data);
        if (paymentSuccess) {
          setPendingConfirmation(!!data.locked);
          if (!data.locked) {
            setToaster({ message: ToasterTrans[language].coursePurchased, type: "success", show: true });
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
          setProcessingPayment(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSlug, token, userLoading]);

  const handleRefreshAccess = async () => {
    setRefreshing(true);
    try {
      const data = await getCourse(courseSlug, token);
      setCourse(data);
      if (!data.locked) {
        setPendingConfirmation(false);
        setToaster({ message: ToasterTrans[language].coursePurchased, type: "success", show: true });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  const handlePurchase = async (provider) => {
    if (disablePayment) {
      setToaster({ message: ToasterTrans[language].comingSoon, type: "info", show: true });
      return;
    }
    setPurchasingProvider(provider);
    setPurchaseError("");
    try {
      const { paymentUrl } = await purchaseCourse(courseSlug, provider, token);
      window.location.href = paymentUrl;
    } catch (err) {
      console.error(err);
      setPurchaseError(t.purchaseError);
      setPurchasingProvider(null);
    }
  };

  if (userLoading || !token || isLoading) {
    return (
      <Loader
        load
        message={processingPayment ? ScheduleTrans[language].processingPayment : undefined}
      />
    );
  }

  if (!course || !course.locked) {
    return children;
  }

  if (pendingConfirmation) {
    return (
      <main className="landing-page min-h-screen w-full font-sans antialiased flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center bg-warm-white p-8 rounded-sm border border-sand-dark">
          <h1 className="mb-3 font-serif text-2xl font-medium text-ink">{t.stillProcessingTitle}</h1>
          <p className="mb-6 text-base font-sans font-light text-ink-mid">{t.stillProcessingBody}</p>
          <button
            type="button"
            onClick={handleRefreshAccess}
            disabled={refreshing}
            className="rounded-sm border border-sand-dark bg-warm-white px-6 py-3 font-serif text-base text-ink transition hover:border-clay/50 disabled:opacity-50"
          >
            {refreshing ? t.checking : t.refresh}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="landing-page min-h-screen w-full font-sans antialiased">
      {/* Intro */}
      <section className="bg-warm-white py-12 mt-10 md:py-14 px-6 md:px-20 w-full border-b border-sand-dark/20">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="mb-4 md:mb-6 text-center font-serif text-3xl md:text-4xl font-medium text-ink">
            {course.title}
          </h1>
          <img
            src={PREVIEW_IMAGE_URL}
            alt={course.title}
            className="mb-6 md:mb-8 w-full rounded-sm border border-sand-dark object-cover"
          />
          <div className="bg-warm-white p-6 md:p-8 rounded-sm border border-sand-dark">
            {course.description && (
              <p className="text-base font-sans font-light text-ink-mid mb-4">
                {course.description}
              </p>
            )}
            {(course.nisPrice > 0 || course.rubPrice > 0) && course.eurDisplayPrice > 0 && (
              <p className="mt-4 font-serif text-2xl font-medium text-ink">
                <ApproxPrice
                  eur={course.eurDisplayPrice}
                  charges={[{ amount: course.nisPrice, currency: 'ILS' }, { amount: course.rubPrice, currency: 'RUB' }]}
                />
              </p>
            )}
            {(course.nisPrice > 0 || course.rubPrice > 0) && !(course.eurDisplayPrice > 0) && (
              <p className="mt-4 font-serif text-2xl font-medium text-ink">
                {course.nisPrice > 0 && `${course.nisPrice} ₪`}
                {course.nisPrice > 0 && course.rubPrice > 0 && (
                  <span className="text-ink-mid"> / </span>
                )}
                {course.rubPrice > 0 && (
                  <span className={course.nisPrice > 0 ? "text-ink-mid" : ""}>
                    {course.rubPrice} ₽
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Choose payment option */}
      {(course.nisPrice > 0 || course.rubPrice > 0) && (
        <section className="bg-cream py-12 md:py-16 px-6 md:px-20 w-full border-t border-sand-dark/30">
          <div className="max-w-6xl mx-auto">
            <h2 className="mb-6 md:mb-8 text-center font-serif text-2xl md:text-3xl font-medium text-ink">
              {t.choosePayment}
            </h2>
            {purchaseError && (
              <p className="mb-6 text-center text-sm text-red-600">{purchaseError}</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              {[
                course.nisPrice > 0 && {
                  provider: "allpay",
                  title: SuccessTrans[language].interCard,
                  img: PayRub,
                },
                course.rubPrice > 0 && {
                  provider: "yookassa",
                  title: SuccessTrans[language].rusCard,
                  img: PayShek,
                },
              ].filter(Boolean).map((item) => (
                <button
                  key={item.provider}
                  type="button"
                  onClick={() => handlePurchase(item.provider)}
                  disabled={!!purchasingProvider}
                  className="bg-warm-white rounded-sm border border-sand-dark p-8 md:py-10 text-center transition hover:border-clay/50 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center"
                >
                  <img src={item.img} alt={item.title} className="rounded-full w-24 h-24 object-cover mb-4" />
                  <h3 className="font-serif text-xl font-medium text-ink">
                    {purchasingProvider === item.provider ? t.redirecting : item.title}
                  </h3>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
